import type { ReactElement, SVGProps } from "react";

// Icon set — transcribed 1:1 from the founder's iconography sheet
// (docs/design/admin-components/06-ikonografi.html): a single outline set drawn on a
// 24px grid, 1.7px stroke, round caps + joins. Rendered inline as SVG with
// `stroke: currentColor` so tone comes from the surrounding text/color context.
// Utility glyphs used by the other component files (07–14) are included alongside the
// 24 named concept icons. No copy, no logic — a pure presentational primitive.

export type IconName =
  // 06 — concept icons
  | "protection"
  | "vault"
  | "household"
  | "member"
  | "document"
  | "suggestion"
  | "scorecard"
  | "health"
  | "alert"
  | "calendar"
  | "performance"
  | "tasks"
  | "conversation"
  | "home"
  | "dashboard"
  | "brand"
  | "records"
  | "tier"
  | "edit"
  | "quality"
  | "measure"
  | "coverage"
  // utility glyphs (07–14)
  | "arrow-right"
  | "check"
  | "check-circle"
  | "chevron-down"
  | "chevron-up"
  | "chevron-left"
  | "chevron-right"
  | "search"
  | "plus"
  | "close"
  | "info"
  | "alert-circle"
  | "alert-triangle"
  | "lock"
  | "trash"
  | "filter"
  | "file"
  | "download"
  | "trend-up"
  | "star";

const PATHS: Record<IconName, ReactElement> = {
  protection: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  vault: (
    <>
      <path d="M5 11h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  household: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  member: (
    <>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <path d="M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
    </>
  ),
  document: (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
      <path d="M14 2v5h5" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </>
  ),
  suggestion: (
    <>
      <path d="M9.94 14.94A2 2 0 0 0 8.5 13.5l-5.6-1.44a.5.5 0 0 1 0-.96L8.5 9.66a2 2 0 0 0 1.44-1.44l1.44-5.6a.5.5 0 0 1 .96 0l1.44 5.6a2 2 0 0 0 1.44 1.44l5.6 1.44a.5.5 0 0 1 0 .96l-5.6 1.44a2 2 0 0 0-1.44 1.44l-1.44 5.6a.5.5 0 0 1-.96 0z" />
      <path d="M19 3v4" />
      <path d="M21 5h-4" />
    </>
  ),
  scorecard: (
    <>
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </>
  ),
  health: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  alert: (
    <>
      <path d="M10.268 21a2 2 0 0 0 3.464 0" />
      <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
    </>
  ),
  calendar: (
    <>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <path d="M3 10h18" />
      <path d="M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
    </>
  ),
  performance: (
    <>
      <path d="M16 7h6v6" />
      <path d="m22 7-8.5 8.5-5-5L2 17" />
    </>
  ),
  tasks: (
    <>
      <path d="M3 5h2" />
      <path d="m3 11 1.2 1.2L7 9.5" />
      <path d="M3 19h2" />
      <path d="M10 5h11" />
      <path d="M10 12h11" />
      <path d="M10 19h11" />
    </>
  ),
  conversation: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  home: (
    <>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </>
  ),
  dashboard: (
    <>
      <path d="M3 3h7v9H3z" />
      <path d="M14 3h7v5h-7z" />
      <path d="M14 12h7v9h-7z" />
      <path d="M3 16h7v5H3z" />
    </>
  ),
  brand: (
    <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z" />
  ),
  records: (
    <>
      <path d="M12 3v18" />
      <path d="M3 9h18" />
      <path d="M3 15h18" />
      <path d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </>
  ),
  tier: (
    <>
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <path d="M7.5 7.5h.01" />
    </>
  ),
  edit: (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
    </>
  ),
  quality: (
    <path d="M11.5 3.6a.6.6 0 0 1 1 0l2.4 5 5.3.8a.6.6 0 0 1 .3 1l-3.8 3.7.9 5.3a.6.6 0 0 1-.86.63L12 18.3l-4.7 2.5a.6.6 0 0 1-.87-.63l.9-5.3-3.8-3.7a.6.6 0 0 1 .3-1l5.3-.8z" />
  ),
  measure: (
    <>
      <path d="M21.3 8.7 8.7 21.3a1 1 0 0 1-1.4 0l-4.6-4.6a1 1 0 0 1 0-1.4L15.3 2.7a1 1 0 0 1 1.4 0l4.6 4.6a1 1 0 0 1 0 1.4Z" />
      <path d="m7.5 10.5 2 2" />
      <path d="m10.5 7.5 2 2" />
      <path d="m13.5 4.5 2 2" />
    </>
  ),
  coverage: (
    <>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </>
  ),
  "arrow-right": (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  "check-circle": (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  "chevron-down": <path d="m6 9 6 6 6-6" />,
  "chevron-up": <path d="m18 15-6-6-6 6" />,
  "chevron-left": <path d="m15 18-6-6 6-6" />,
  "chevron-right": <path d="m9 18 6-6-6-6" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.34-4.34" />
    </>
  ),
  plus: (
    <>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </>
  ),
  close: (
    <>
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </>
  ),
  "alert-circle": (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </>
  ),
  "alert-triangle": (
    <>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </>
  ),
  lock: (
    <>
      <path d="M5 11h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  trash: (
    <>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </>
  ),
  filter: (
    <>
      <path d="M3 6h18" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </>
  ),
  file: (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
      <path d="M14 2v5h5" />
    </>
  ),
  // A12 — the CSV export affordance. Same 24px/stroke geometry as the set.
  download: (
    <>
      <path d="M12 3v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M5 21h14" />
    </>
  ),
  "trend-up": <path d="m6 17 5-5 4 4 5-7" />,
  star: (
    <path d="M11.5 3.6a.6.6 0 0 1 1 0l2.4 5 5.3.8a.6.6 0 0 1 .3 1l-3.8 3.7.9 5.3a.6.6 0 0 1-.86.63L12 18.3l-4.7 2.5a.6.6 0 0 1-.87-.63l.9-5.3-3.8-3.7a.6.6 0 0 1 .3-1l5.3-.8z" />
  ),
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  /** px size (defaults to the 24px grid). */
  size?: number;
  /** Stroke weight — 1.7 is the sheet default; callers bump it for emphasis. */
  strokeWidth?: number;
}

export function Icon({ name, size = 24, strokeWidth = 1.7, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
